import {
  ClickAwayListener,
  Drawer,
  makeStyles,
  Popper,
  Tooltip as MuiTooltip,
  Avatar,
  Button,
  ConfirmationDialog,
  DropdownSelect,
  IconButton,
  Input,
  Paper,
  Typography,
  useClientOptions,
  useDatePickerStyles,
  useDialogs,
  useIsMobile,
  DateRangePickerController
} from '@paintscout/ui'
import type { DropdownSelectOption } from '@paintscout/ui'
import type { CalendarEvent } from 'paintscout'
import React, { useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import { formatCurrency, formatName, formatPhoneNumber, getCrews, getTimezone } from '@paintscout/util/builder'
import moment from 'moment-timezone'
import classNames from 'classnames'
import PencilIcon from '@material-ui/icons/CreateOutlined'
import { goToContact, goToDeal } from 'src/util/routing'
import { useProductionCalendar } from './ProductionCalendarContext'

const DATE_FORMAT_FULL = 'MMM D, YYYY'
const NO_PROJECT_MANAGER = 'No Project Manager'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    minWidth: theme.typography.pxToRem(400),
    [theme.breakpoints.down('xs')]: {
      border: 'none',
      minWidth: '100vw'
    }
  },
  paper: {
    borderRadius: theme.borderRadius.md,
    border: 'none'
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  iconButton: {
    padding: 0,
    '& svg': {
      width: '18px',
      height: '18px'
    }
  },
  listContent: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(0.75),
    gap: theme.spacing(0.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`,
    marginBottom: theme.spacing(1.5)
  },
  smallText: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  smallerText: {
    fontSize: theme.typography.pxToRem(10)
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      color: theme.paintscout.palette.primary
    },
    '&:hover $dealName': {
      color: theme.paintscout.palette.primary
    }
  },
  dealName: {}, // for the above
  cancelButton: {
    fontWeight: 500,
    marginLeft: theme.spacing(-1.5),
    '&:hover span': {
      color: `${theme.palette.error.main}`
    }
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '12px',
    marginRight: theme.spacing(1)
  },
  expandedSection: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P94}`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing(1.5, 1),
    margin: theme.spacing(0.5, 0)
  },
  progressInputHeight: {
    height: theme.typography.pxToRem(32)
  },
  progressInput: {
    width: '48px',
    paddingRight: theme.spacing(0.5)
  },
  endAdornment: {
    marginRight: theme.spacing(0.5)
  },
  datePickerFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1)
  },
  datePicker: {
    boxShadow: 'none',
    '& div': {
      backgroundColor: 'transparent !important'
    },
    '& .CalendarDay:not(.CalendarDay__selected_span)': {
      backgroundColor: 'transparent'
    }
  },

  avatar: {
    padding: '0px !important',
    marginLeft: '2px'
  },
  crewAvatarIcon: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.common.white,
    display: 'block',
    lineHeight: 1,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'geometricPrecision'
  }
}))

interface ProductionEventPopperProps {
  event?: CalendarEvent
  anchorEl?: HTMLElement | null
  onClose: () => void
  closeOnEdit?: boolean
}

export default function ProductionEventPopper({ event, onClose, anchorEl, closeOnEdit }: ProductionEventPopperProps) {
  const classes = useStyles()
  const mobile = useIsMobile({ xs: true })
  const datePickerClasses = useDatePickerStyles({
    isSingle: false
  })
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { openDialog, dismissDialog } = useDialogs()

  const { updateEvent, deleteEvent } = useProductionCalendar()

  const [durationExpanded, setDurationExpanded] = useState(false)
  const [progressExpanded, setProgressExpanded] = useState(false)
  const [initialEvent] = useState<CalendarEvent>(event)

  const isSingleDay = moment(event?.start).tz(timezone).isSame(moment(event?.end).tz(timezone), 'day')
  const formattedDuration = isSingleDay
    ? moment(event?.start).tz(timezone).format(DATE_FORMAT_FULL)
    : `${moment(event?.start).tz(timezone).format(DATE_FORMAT_FULL)} - ${moment(event?.end).tz(timezone).format(DATE_FORMAT_FULL)}`

  const activeCrewOptions = getCrews({ options })
  const allCrewOptions = getCrews({ options, includeTrashed: true })
  const eventCrew = event?.crew ? allCrewOptions.find((crew) => crew._id === event?.crew) : null
  const activeDropdownOptions: DropdownSelectOption[] = activeCrewOptions.map((crew) => ({
    label: crew.name,
    value: crew._id,
    crewColor: crew.color,
    projectManagerName: crew.projectManager ? formatName(crew.projectManager) : NO_PROJECT_MANAGER
  }))
  const dropdownOptions: DropdownSelectOption[] = eventCrew?.trashed
    ? [
        {
          label: `${eventCrew.name} (Deleted)`,
          value: eventCrew._id,
          crewColor: eventCrew.color,
          projectManagerName: eventCrew.projectManager ? formatName(eventCrew.projectManager) : NO_PROJECT_MANAGER,
          disabled: true
        },
        ...activeDropdownOptions
      ]
    : activeDropdownOptions
  const crewOptions = allCrewOptions

  const handleCancelEvent = () => {
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      onConfirm: () => {
        deleteEvent(event)
        dismissDialog()
        onClose()
      },
      onCancel: dismissDialog
    })
  }

  function openMap(address) {
    const encodedAddress = encodeURIComponent(address)
    if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
      window.open(`maps://?q=${encodedAddress}`, '_blank')
    } else {
      window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
    }
  }

  const handleClickAway = (event: React.MouseEvent<Document, MouseEvent>) => {
    try {
      if (anchorEl && anchorEl?.contains(event?.target as Node)) {
        return
      }
    } catch {
      // ignore
    }

    onClose()
  }

  const DurationComponent = () => {
    const [localStart, setLocalStart] = useState<Date>(event?.start || new Date())
    const [localEnd, setLocalEnd] = useState<Date>(event?.end || new Date())

    const handleChange = (startDate: moment.Moment, endDate: moment.Moment) => {
      setLocalStart(startDate.toDate())
      if (endDate) {
        setLocalEnd(endDate.toDate())
      } else {
        setLocalEnd(startDate.toDate())
      }
    }

    const handleSave = () => {
      const updatedEvent = { ...event, start: localStart, end: localEnd }
      updateEvent(updatedEvent)
      setDurationExpanded(false)
      onClose()
    }

    const handleCancel = () => {
      setLocalStart(event?.start || new Date())
      setLocalEnd(event?.end || new Date())
      setDurationExpanded(false)
    }

    if (durationExpanded) {
      return (
        <div className={classNames(classes.expandedSection, datePickerClasses.popper, classes.datePicker)}>
          <DateRangePickerController
            startDate={moment(localStart)}
            endDate={moment(localEnd)}
            onChange={handleChange}
            minimumNights={0}
          />
          <div className={classes.datePickerFooter}>
            <Button variant="textNoPadding" size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="small" onClick={handleSave} className={classes.progressInputHeight}>
              Save
            </Button>
          </div>
        </div>
      )
    }
    return (
      <Typography variant="body1" color="textSecondary" className={classes.spaceBetween}>
        <div className={classes.smallText}>
          <i className={classNames(classes.icon, 'fa-regular fa-clock')}></i>
          {formattedDuration}
        </div>
        <IconButton className={classes.iconButton} onClick={() => setDurationExpanded(true)}>
          <PencilIcon />
        </IconButton>
      </Typography>
    )
  }

  const ProgressComponent = () => {
    const [localProgress, setLocalProgress] = useState<number>(event?.progress || 0)
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newProgress = parseInt(e.target.value, 10)
      const value = isNaN(newProgress) ? undefined : Math.min(newProgress, 100)

      setLocalProgress(value)
    }

    const handleSave = () => {
      const updatedEvent = { ...event, progress: localProgress || 0, previousProgress: initialEvent?.progress || 0 }
      updateEvent(updatedEvent)
      setProgressExpanded(false)
      if (closeOnEdit) {
        onClose()
      }
    }

    const handleCancel = () => {
      setLocalProgress(event?.progress || 0)
      setProgressExpanded(false)
    }

    if (progressExpanded) {
      return (
        <div className={classes.expandedSection}>
          <div className={classes.spaceBetween}>
            <div className={classes.spaceBetween}>
              <i className={classNames(classes.icon, 'fa-regular fa-arrow-trend-up')}></i>
              <Input
                classes={{ root: classes.progressInputHeight, input: classes.progressInput }}
                type="number"
                value={localProgress}
                placeholder={'0'}
                defaultValue={0}
                inputProps={{ min: 0, max: 100 }}
                endAdornment={<span className={classNames(classes.smallText, classes.endAdornment)}>%</span>}
                onChange={handleChange}
              />
            </div>
            <div className={classes.spaceBetween}>
              <Button variant="textNoPadding" size="small" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="small" onClick={handleSave} className={classes.progressInputHeight}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )
    }
    return (
      <Typography variant="body1" color="textSecondary" className={classes.spaceBetween}>
        <div className={classes.smallText}>
          <i className={classNames(classes.icon, 'fa-regular fa-arrow-trend-up')}></i>
          {event?.progress}% complete
        </div>
        <IconButton className={classes.iconButton} onClick={() => setProgressExpanded(true)}>
          <PencilIcon />
        </IconButton>
      </Typography>
    )
  }

  const Content = () => {
    return (
      <Paper classes={{ root: classes.root }}>
        <div className={classes.spaceBetween}>
          <a href="#" className={classes.link} onClick={(e) => e.preventDefault()}>
            <Typography
              className={classes.dealName}
              variant={'h3'}
              weight={500}
              onClick={() => goToDeal(event?.deal.id)}
            >
              {event?.deal?.name}
            </Typography>
          </a>
          <IconButton className={classes.iconButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.listContent}>
          {event?.contact && (
            <Typography variant="body1" color="textSecondary" className={classes.smallText}>
              <i className={classNames(classes.icon, 'fa-regular fa-user')}></i>
              <a
                href="#"
                className={classes.link}
                onClick={(e) => {
                  e.stopPropagation()
                  goToContact(event?.contact?.id)
                }}
              >
                {formatName(event?.contact)}
              </a>
            </Typography>
          )}
          {event?.contact?.phoneNumber && (
            <Typography variant="body1" color="textSecondary" className={classes.smallText}>
              <i className={classNames(classes.icon, 'fa-regular fa-phone')}></i>
              <a
                href="#"
                className={classes.link}
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(`tel:${event?.contact?.phoneNumber}`, '_blank')
                }}
              >
                {formatPhoneNumber({ options, phoneNumber: event?.contact?.phoneNumber || '' })}
              </a>
            </Typography>
          )}
          {event?.address && (
            <Typography variant="body1" color="textSecondary" className={classes.smallText}>
              <i className={classNames(classes.icon, 'fa-regular fa-location-dot')}></i>
              <a
                href="#"
                className={classes.link}
                onClick={(e) => {
                  e.stopPropagation()
                  openMap(event?.address || '')
                }}
              >
                {event?.address}
              </a>
            </Typography>
          )}
        </div>
        <div className={classes.listContent}>
          {formattedDuration && <DurationComponent />}
          <ProgressComponent />
          <Typography variant="body1" color="textSecondary" className={classes.smallText}>
            <i className={classNames(classes.icon, 'fa-regular fa-dollar-sign')}></i>
            Total: {formatCurrency({ options, value: event?.deal?.value || 0 })}
          </Typography>
          <Typography variant="body1" color="textSecondary" className={classes.smallText}>
            <i className={classNames(classes.icon, 'fa-regular fa-stopwatch')}></i>
            Estimated Hours: {(Math.round(event?.deal?.hours || 0) * 100) / 100} hrs
          </Typography>
          <Typography variant="body1" color="textSecondary" className={classes.smallText}>
            <i className={classNames(classes.icon, 'fa-light fa-users')}></i>
            Crew:{' '}
            <DropdownSelect
              variant="single"
              options={dropdownOptions}
              onChange={(option) => {
                const selectedCrew = crewOptions.find((crew) => crew._id === option.value)
                if (selectedCrew) {
                  const updatedEvent = { ...event, crew: selectedCrew._id }
                  updateEvent(updatedEvent)
                  if (closeOnEdit) {
                    onClose()
                  }
                }
              }}
              value={dropdownOptions.find((option) => option.value === eventCrew?._id)}
              formatOptionLabel={(option, placement) => {
                const crewOption = crewOptions.find((crew) => crew._id === option.value)
                const crewAvatarColor = option.crewColor || crewOption?.color
                const projectManagerName = option.projectManagerName || NO_PROJECT_MANAGER
                const isMenuItem = placement === 'menu-item'
                const content = (
                  <Typography className={classes.smallText} color="textPrimary">
                    <Avatar
                      className={classes.avatar}
                      disableTooltip
                      style={{
                        width: isMenuItem ? '32px' : '20px',
                        height: isMenuItem ? '32px' : '20px',
                        backgroundColor: crewAvatarColor,
                        color: '#fff',
                        transform: 'none'
                      }}
                    >
                      <i
                        className={`fa-light fa-users ${classes.crewAvatarIcon}`}
                        style={{ fontSize: isMenuItem ? '14px' : '10px' }}
                      />
                    </Avatar>
                    <div>
                      {option.label}{' '}
                      {placement === 'menu-item' && (
                        <Typography variant="subtitle1" className={classes.smallerText}>
                          Project Manager: {projectManagerName}
                        </Typography>
                      )}
                    </div>
                  </Typography>
                )
                if (option.disabled && isMenuItem) {
                  return (
                    <MuiTooltip title={`${crewOption?.name ?? option.label} (Deleted)`}>
                      <span style={{ display: 'block', pointerEvents: 'auto' }}>{content}</span>
                    </MuiTooltip>
                  )
                }
                return content
              }}
            />
          </Typography>
        </div>
        <Button variant="textNoPadding" size="small" className={classes.cancelButton} onClick={handleCancelEvent}>
          Delete
        </Button>
      </Paper>
    )
  }

  const MemoizedContent = React.useMemo(() => <Content />, [event, durationExpanded, progressExpanded])

  if (mobile) {
    return (
      <Drawer
        anchor="bottom"
        open={Boolean(anchorEl)}
        onClose={onClose}
        disableScrollLock
        classes={{ paper: classes.paper }}
      >
        {MemoizedContent}
      </Drawer>
    )
  }

  return (
    <Popper
      key={event?.id}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      id={`preview-popper-${event?.id}`}
      style={{ zIndex: 2000 }}
    >
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <div>{MemoizedContent}</div>
      </ClickAwayListener>
    </Popper>
  )
}
